
import { useState } from 'react';
import { useRSVP } from '../../context/RSVPContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RSVPButton } from '@/components/ui/rsvp-button';
import { EventDetailsBottomDrawer } from '@/components/event-details/drawers/EventDetailsBottomDrawer';
import { Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Database } from '@/integrations/supabase/types';
import { GuestData } from '../../types';

type TimeOfDay = Database["public"]["Enums"]["time_of_day"];

export const GuestSelectionStep = () => {
  const { 
    availableGuests, 
    inProgressGuests,
    selectedGuest, 
    setSelectedGuest, 
    goToNextStep, 
    eventData,
    currentState 
  } = useRSVP();
  const [showEventDetails, setShowEventDetails] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSelectGuest = (guest: GuestData) => {
    setSelectedGuest(guest);
  };

  const handleContinue = () => {
    if (selectedGuest) {
      goToNextStep();
    }
  };

  const openEventDetails = () => {
    setShowEventDetails(true);
  };

  // Convert string[] to TimeOfDay[] safely
  const convertToTimeOfDay = (times: string[]): TimeOfDay[] => {
    return times.filter((time): time is TimeOfDay => 
      time === 'morning' || time === 'afternoon' || time === 'evening'
    );
  };

  // Format the claimed date
  const formatClaimedDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    
    try {
      return format(new Date(dateString), 'MMM d');
    } catch (error) {
      return '';
    }
  };

  // All guests to display (both available and in-progress)
  const allGuests = [...availableGuests, ...inProgressGuests];

  return (
    <div className="flex flex-col w-full h-full">
      <div className="text-center py-[15vh]">
        <h1 className="text-2xl font-bold mb-2">
          Select Invite
        </h1>

        <Button variant="link" className="text-muted-foreground" onClick={openEventDetails}>
          View details
        </Button>
      </div>
      
      {/* Guest list */}
      <ScrollArea className="flex-1 px-4 pb-[20vh]">
        <div className="space-y-2">
          {allGuests.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">
              No available guests
            </p>
          ) : (
            allGuests.map(guest => {
              const isInProgress = guest.claimed && !guest.hasResponded;
              
              return (
                <button
                  key={guest.inviteId}
                  className={`w-full p-4 text-center rounded-lg transition-colors ${
                    selectedGuest?.inviteId === guest.inviteId 
                      ? "bg-primary text-primary-foreground" 
                      : isInProgress 
                        ? "bg-amber-50 hover:bg-amber-100 border border-amber-200"
                        : "bg-secondary hover:bg-secondary/80"
                  }`}
                  onClick={() => handleSelectGuest(guest)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Avatar className={`h-8 w-8 overflow-hidden ${
                        selectedGuest?.inviteId === guest.inviteId
                          ? "bg-white text-black border-2 border-white"
                          : "bg-white text-black border-2 border-black"
                      }`}>
                        {guest.avatar_url ? (
                          <AvatarImage 
                            src={guest.avatar_url} 
                            alt={guest.displayName}
                          />
                        ) : null}
                        <AvatarFallback>
                          {getInitials(guest.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-lg">{guest.displayName}</span>
                    </div>
                    
                    {isInProgress && (
                      <div className="flex items-center text-amber-600 text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>
                          {guest.claimedByName 
                            ? `Started by ${guest.claimedByName.split(' ')[0]}` 
                            : `Started ${formatClaimedDate(guest.claimedAt)}`}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Fixed button at bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-4 pb-12 bg-white">
        <div className="max-w-2xl mx-auto px-4">
          <RSVPButton
            onClick={handleContinue}
            disabled={!selectedGuest}
            className="mx-auto block"
          >
            Start RSVP
          </RSVPButton>
        </div>
      </div>

      {/* Event Details Drawer */}
      {eventData && (
        <EventDetailsBottomDrawer
          open={showEventDetails}
          onOpenChange={setShowEventDetails}
          event={{
            name: eventData.name,
            description: eventData.description || "",
            event_date: eventData.preferredDates[0] || "",
            image_url: eventData.imageUrl,
            status: "active",
            location_type: "flexible",
            flexible_location_description: "",
            preferred_dates: eventData.preferredDates,
            preferred_venues: [],
            preferred_time_of_day: convertToTimeOfDay(eventData.preferredTimes),
            host: {
              full_name: eventData.hostName,
            },
            event_id: eventData.id,
            event_deadline: eventData.event_deadline,
            event_images: eventData.eventImages
          }}
        />
      )}
    </div>
  );
};
